import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, MapPin, Globe, Mail, Phone, Pencil, Calendar, Trophy, Facebook, Instagram, Crown, Users } from "lucide-react";
import { PublicGallery, PublicAchievements } from "@/components/EntityEditShared";
import { PublicEntityLinks } from "@/components/EntityLinks";
import { EntityBadge } from "@/components/EntityBadge";
import { ClaimPageButton } from "@/components/ClaimPageButton";
import { EntityAffiliationsPanel } from "@/components/EntityAffiliationsPanel";
import { SupportCampaignBanner } from "@/components/SupportCampaignBanner";

export default function ClubDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [fed, setFed] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clubs/${slug}`);
        if (cancelled) return;
        setClub(data);
        if (data.federation_id) {
          api.get(`/federations/${data.federation_id}`).then((r) => !cancelled && setFed(r.data)).catch(() => {});
        }
        // Charger liens entrants (athlètes validés vers ce club)
        api.get(`/entity-links?target_type=club&target_id=${data.id}&status=accepted&limit=200`)
          .then((r) => !cancelled && setIncoming(r.data?.items || []))
          .catch(() => {});
      } catch (e) {
        if (e?.response?.status === 404) navigate("/clubs", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (loading) return <div className="max-w-5xl mx-auto py-8 px-4 space-y-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-24 w-full" /></div>;
  if (!club) return null;

  const isOwner = user && (user.id === club.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r)));
  const athletes = incoming.filter((l) => l.source_type === "athlete");
  const coaches = incoming.filter((l) => l.source_type === "coach");

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-5" data-testid="club-detail-page">
      {/* Header cover */}
      <div className="relative h-48 rounded-xl overflow-hidden bg-gradient-to-br from-[hsl(var(--aito-black))] to-[hsl(var(--aito-lagoon-2))]">
        {/* V11.13 — Cover réelle si disponible, sinon fallback bannière Club AITO.PF */}
        <img
          src={club.cover_url || "/branding/cover-club-default.png"}
          alt=""
          className="object-cover w-full h-full opacity-50"
          onError={(e) => { e.currentTarget.src = "/branding/cover-club-default.png"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 flex-wrap">
          <div className="flex items-end gap-3 min-w-0">
            <div className="h-20 w-20 rounded-xl bg-background border-2 border-background shadow-lg flex items-center justify-center overflow-hidden shrink-0">
              {club.logo_url ? <img src={club.logo_url} alt={club.name} className="object-cover w-full h-full" /> : <Building2 className="h-9 w-9 text-muted-foreground" />}
            </div>
            <div className="text-white min-w-0">
              <h1 className="font-display text-3xl font-bold drop-shadow" data-testid="club-name">{club.name}</h1>
              {club.short_motto && <p className="text-sm opacity-90 italic">« {club.short_motto} »</p>}
              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                {club.discipline && <Badge variant="secondary" className="capitalize">{club.discipline}</Badge>}
                {(club.disciplines || []).filter((d) => d !== club.discipline).map((d) => (
                  <Badge key={d} variant="outline" className="text-white border-white/30 capitalize">{d}</Badge>
                ))}
                {(club.island || club.commune) && (
                  <span className="flex items-center gap-0.5 opacity-90"><MapPin className="h-3 w-3" />{[club.commune, club.island].filter(Boolean).join(" · ")}</span>
                )}
                {club.founded_year && <span className="flex items-center gap-0.5 opacity-90"><Calendar className="h-3 w-3" />Fondé en {club.founded_year}</span>}
              </div>
            </div>
          </div>
          {isOwner && (
            <Link to={`/clubs/${club.id}/edit`} data-testid="club-edit-btn">
              <Button size="sm" variant="default" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
              </Button>
            </Link>
          )}
          {user && !isOwner && (
            <ClaimPageButton entityType="club" entityId={club.id} entityName={club.name} />
          )}
        </div>
      </div>

      {/* V9.19 — Campagne de soutien SupportCampaign V1 */}
      <SupportCampaignBanner beneficiaryType="club" beneficiaryId={club.id} />

      {/* V9.13 — Tabs masqués si vides sur la fiche publique (règle masquage). */}
      <Tabs defaultValue="overview">
        <TabsList data-testid="club-tabs">
          <TabsTrigger value="overview" data-testid="club-tab-overview">Présentation</TabsTrigger>
          {isOwner && (
            <TabsTrigger value="affiliations" data-testid="club-tab-affiliations">Affiliations</TabsTrigger>
          )}
          {(athletes.length + coaches.length) > 0 && (
            <TabsTrigger value="network" data-testid="club-tab-network">
              Membres validés ({athletes.length + coaches.length})
            </TabsTrigger>
          )}
          {(club.achievements || []).length > 0 && (
            <TabsTrigger value="palmares" data-testid="club-tab-palmares">
              Palmarès ({(club.achievements || []).length})
            </TabsTrigger>
          )}
          {(club.gallery || []).length > 0 && (
            <TabsTrigger value="gallery" data-testid="club-tab-gallery">
              Galerie ({(club.gallery || []).length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {club.description && (
            <Card className="p-5">
              <h2 className="font-display font-semibold mb-2">À propos</h2>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{club.description}</p>
            </Card>
          )}
          {/* Contact / Affiliation — masqués si tout est vide */}
          {(club.contact_email || club.contact_phone || club.website || club.address || fed) && (
            <div className="grid md:grid-cols-2 gap-4">
              {(club.contact_email || club.contact_phone || club.website || club.address || club.social_facebook || club.social_instagram) && (
                <Card className="p-5 space-y-2">
                  <h3 className="font-display font-semibold text-sm">Contact</h3>
                  {club.address && <p className="text-xs flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" />{club.address}</p>}
                  {club.contact_email && <a href={`mailto:${club.contact_email}`} className="text-xs flex items-center gap-1.5 hover:underline"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{club.contact_email}</a>}
                  {club.contact_phone && <p className="text-xs flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{club.contact_phone}</p>}
                  {club.website && <a href={club.website} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1.5 hover:underline"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{club.website}</a>}
                  {(club.social_facebook || club.social_instagram) && (
                    <div className="flex items-center gap-2 pt-1">
                      {club.social_facebook && <a href={club.social_facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-foreground"><Facebook className="h-4 w-4" /></a>}
                      {club.social_instagram && <a href={club.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-foreground"><Instagram className="h-4 w-4" /></a>}
                    </div>
                  )}
                </Card>
              )}
              {fed && (
                <Card className="p-5 space-y-2">
                  <h3 className="font-display font-semibold text-sm">Affiliation</h3>
                  <EntityBadge
                    kind="federation"
                    name={fed.name}
                    slug={fed.slug}
                    image={fed.logo_url}
                    link_status="accepted"
                    size="md"
                    variant="row"
                  />
                  <div className="text-[11px] text-muted-foreground pl-11">Fédération de tutelle</div>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {isOwner && (
          <TabsContent value="affiliations" className="mt-4 space-y-4">
            <EntityAffiliationsPanel entityType="club" entityId={club.id} canManage={isOwner} />
          </TabsContent>
        )}

        {(athletes.length + coaches.length) > 0 && (
          <TabsContent value="network" className="mt-4 space-y-4">
            {coaches.length > 0 && (
              <Card className="p-5">
                <h3 className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5"><Users className="h-4 w-4" />Coachs ({coaches.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {coaches.map((l) => (
                    <EntityBadge
                      key={l.id}
                      kind="coach"
                      name={l.source_name || "Coach"}
                      slug={l.source_slug || l.source_id}
                      image={l.source_image}
                      link_status="accepted"
                      size="sm"
                      variant="chip"
                    />
                  ))}
                </div>
              </Card>
            )}
            {athletes.length > 0 && (
              <Card className="p-5">
                <h3 className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5"><Trophy className="h-4 w-4" />Athlètes ({athletes.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {athletes.map((l) => (
                    <EntityBadge
                      key={l.id}
                      kind="athlete"
                      name={l.source_name || "Athlète"}
                      slug={l.source_slug || l.source_id}
                      image={l.source_image}
                      link_status="accepted"
                      size="sm"
                      variant="chip"
                    />
                  ))}
                </div>
              </Card>
            )}
          </TabsContent>
        )}

        {(club.achievements || []).length > 0 && (
          <TabsContent value="palmares" className="mt-4">
            <Card className="p-5"><PublicAchievements items={club.achievements} /></Card>
          </TabsContent>
        )}

        {(club.gallery || []).length > 0 && (
          <TabsContent value="gallery" className="mt-4">
            <Card className="p-5"><PublicGallery items={club.gallery} /></Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Si fédération de tutelle, lien sortant validé */}
      {fed && (
        <PublicEntityLinks sourceType="club" sourceId={club.id} fallbackLegacy={null} />
      )}
    </div>
  );
}
